import { CLDImageAsset, CLDVideoAsset, PlayerConf, Transformation } from "_types"
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

/**
 * @name updateCloudinarySize
 *
 * @description
 * This method refines the received URL with an updated width and height (if required).
 *
 * @param {string} url
 * @param {number} width
 * @param {number | undefined} [height]
 * @returns {string} Final url
 */
export const updateCloudinarySize = (url: string, width: number, height?: number): string => {
    if (!url) return url
    // Build transformation string
    const transformations = [`w_${width}`]
    transformations.push(`dpr_auto`)
    if (height) transformations.push(`h_${height}`)

    // Remove any existing w_ or h_ from the transformation section
    return url
        .replace(/(\/upload\/)([^/]+)?/, (_, prefix: string, trans: string = '') => {
            const filtered = trans ? trans.split(',').filter((t) => !t.startsWith('w_') && !t.startsWith('h_') && !t.startsWith('dpr_')) : []

            let finalTrans = ''
            const hasImageProcessingParams = /f_auto|q_auto|c_fill|g_face|w_|h_|dpr_/.test(trans)
            if (hasImageProcessingParams) {
                finalTrans = [...filtered, ...transformations].join(',')
            } else {
                finalTrans = [...transformations, ...filtered].join('/')
            }
            return `${prefix}${finalTrans ? finalTrans : ''}`
        })
}

export const updateCloudinaryPDImage = (image: CLDImageAsset, globalTransformation: Transformation, cloudinaryCNAME?: string): string => {
    var url = image.imageUrl;
    // Build transformation string

    if (cloudinaryCNAME && cloudinaryCNAME !== 'res.cloudinary.com' && url.indexOf('res.cloudinary.com') > 0) {
        url = url.replace('res.cloudinary.com', cloudinaryCNAME);
    }

    const srcSplitUrl = url.replace(/\/(?:(?:dpr|f|q)_[^,/]+[,/*]?)+/g, '/')
        .split('image/upload/');
    var finalUrl = srcSplitUrl[0] + 'image/upload';
    const transformationObj: Transformation[] = JSON.parse(image.transformation);
    // isTransformationOverride identify advanced video configuration on page designer
    if (image.isTransformationOverride) {
        var overridedRawTrasformations = transformationObj.map(t => t.raw_transformation).join('/')
        return finalUrl.concat('/' + overridedRawTrasformations + '/' + srcSplitUrl[1]);
    } else {
        // image.transformation, if not isTransformationOverride contains preference data configuration at the moment element was inserted in page
        // so transformation should be taken from preferences to be updated with last values
        // anyway in raw_transformation could be contained parameters applied during insertion that should be mantained 
        var rawTransformations = transformationObj.map(t => t.raw_transformation)
        const transformations: string[] = [];
        if (globalTransformation.dpr && globalTransformation.dpr !== 'none') {
            transformations.push(`dpr_${globalTransformation.dpr}`);
        }   
              
        if (globalTransformation.fetchFormat && globalTransformation.fetchFormat !== 'none') {
            transformations.push(`f_${globalTransformation.fetchFormat}`);
        }    

        if (globalTransformation.quality && globalTransformation.quality !== 'none') {
            transformations.push(`q_${globalTransformation.quality}`);
        }

        finalUrl = finalUrl.concat('/' + transformations.join(','))
        if (rawTransformations.length && rawTransformations[0]){
            finalUrl = finalUrl.concat('/' + rawTransformations.join('/'))
        }
        return finalUrl.concat('/' + srcSplitUrl[1])
    }
}

export const updateCloudinaryPDVideo = (video: CLDVideoAsset, globalTransformation: Transformation, cloudinaryCNAME?: string): string => {
    const playerConf: PlayerConf = JSON.parse(video.playerConf);
    const sourceConfig = playerConf?.sourceConfig;
    var url = sourceConfig.video.asset.secure_url; // it contains extension (video.url does not)
    // Build transformation string
    if (cloudinaryCNAME && cloudinaryCNAME !== 'res.cloudinary.com' && url.indexOf('res.cloudinary.com') > 0) {
        url = url.replace('res.cloudinary.com', cloudinaryCNAME);
    }
    const srcSplitUrl = url.replace(/\/(?:(?:br|f|q)_[^,/]+[,/*]?)+/g, '/')
        .split('video/upload/');
    var finalUrl = srcSplitUrl[0] + 'video/upload';
    // isTransformationOverride identify advanced video configuration on page designer
    if (playerConf.isTransformationOverride) {
        var overridedRawTrasformations = sourceConfig.transformation.map(t => t.raw_transformation).join('/')
        return finalUrl.concat('/' + overridedRawTrasformations + '/' + srcSplitUrl[1]);
    } else {
        var rawTransformations = sourceConfig.transformation.map(t => t.raw_transformation)
        const transformations: string[] = [];
        if (globalTransformation.bit_rate && globalTransformation.bit_rate !== 'none' && globalTransformation.bit_rate !== 'default') {
            transformations.push(`br_${globalTransformation.bit_rate}`);
        }   
        // without cloudinary integration url with dynamic format not contains extension, prevent using format with malformed url  
        // if (globalTransformation.fetchFormat && globalTransformation.fetchFormat !== 'none') {
        //     transformations.push(`f_${globalTransformation.fetchFormat}`);
        // }    

        if (globalTransformation.quality && globalTransformation.quality !== 'none') {
            transformations.push(`q_${globalTransformation.quality}`);
        }

        finalUrl = finalUrl.concat('/' + transformations.join(','))
        if (rawTransformations.length && rawTransformations[0]){
            finalUrl = finalUrl.concat('/' + rawTransformations.join('/'))
        }
        return finalUrl.concat('/' + srcSplitUrl[1])
    }
}

export const updateCloudinaryPDPoster = (video: CLDVideoAsset, globalTransformation: Transformation, cloudinaryCNAME?: string): string => {
    const playerConf: PlayerConf = JSON.parse(video.playerConf);
    const sourceConfig = playerConf?.sourceConfig;
    var url = sourceConfig.poster;
    if (cloudinaryCNAME && cloudinaryCNAME !== 'res.cloudinary.com' && url.indexOf('res.cloudinary.com') > 0) {
        url = url.replace('res.cloudinary.com', cloudinaryCNAME);
    }
    // Build transformation string
    const srcSplitUrl = url.replace(/\/(?:(?:dpr|f|q)_[^,/]+[,/*]?)+/g, '/')
        .split('video/upload/');
    var finalUrl = srcSplitUrl[0] + 'video/upload';
    if (globalTransformation.poster_image) {
        finalUrl = finalUrl.concat('/' + globalTransformation.poster_image)
    }

    return finalUrl.concat('/' + srcSplitUrl[1])
}